import React from "react";
import { Box, Button, Typography } from "@mui/material";
import isEqual from "lodash/isEqual";
import ContactFormFields from "./ContactFormFields";
import { Formik, Form, FormikHelpers } from "formik";
import { contactInfoFormValidations } from "./contactInfoFormValidations";
import useShowError from "../../hooks/useShowError";
import { useQueryClient } from "@tanstack/react-query";
import { FormHeading, FormSubHeading, Wrapper } from './ContactForm.styles';
import useShowToast from '../../hooks/useShowToast';
import { useGetContactInfo, useUpdateContactInfo } from "../../hooks/ContactInfo";
import {  ContactInfoUpdate } from "../../api/generated";
import { ERROR_MESSAGES } from "../../constants/commonConstants";
import { openInvoices } from "../../activeScreenSlice";
import { useAppDispatch } from "../../app/hooks"

const ContactForm = () => {
  const showSuccessToast = useShowToast()
  const initialContactInfo: ContactInfoUpdate = {
    primary_contact: {
      full_name: "",
      job_title: "",
      email_address: "",
      phone_number: "",
      department: "",
    },
    technical_contact: {
      full_name: "",
      job_title: "",
      email_address: "",
      phone_number: "",
    },
    billing_contact: {
      legal_entity: "",
      job_title: "",
      full_name: "",
      email_address: "",
      phone_number: "",
      street: "",
      city: "",
      state: "",
      zip: "",
      country: "",
    },
    support_contact: {
      full_name: "",
      email_address: "",
      phone_number: "",
    },
  };
  const queryClient = useQueryClient();
  const { data: contactData, isError: isContactError } = useGetContactInfo();
  const updateContact = useUpdateContactInfo();
  const showError = useShowError();
  const dispatch = useAppDispatch();

  const onSubmit = (
    values: ContactInfoUpdate,
    actions: FormikHelpers<ContactInfoUpdate>
  ) => {
    const originalData = contactData || initialContactInfo;

    if (isEqual(values, originalData)) {
      // No changes, do not trigger API
      actions.setSubmitting(false);
      showSuccessToast("No changes detected.");
      return;
    }

    const mutationMode = updateContact
    mutationMode.mutate(values, {
      onSuccess: (data) => {
        queryClient.setQueryData(["CONTACTINFO"], data);
        actions.setSubmitting(false);
        showSuccessToast("Form Updated successfully")
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          showError(ERROR_MESSAGES.NETWORK);
        } else if (error) {
          const status = error.status;
          if (
            status === 422 &&
            error.body &&
            Array.isArray(error.body.detail)
          ) {
            const details = error.body.detail;
            const messages = details
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              .map((item: any) => {
                return `${item.msg}`;
              })
              .join("\n");
            showError(`${ERROR_MESSAGES.VALIDATION}:${messages}`);
          } else if (status >= 500) {
            showError(ERROR_MESSAGES.SERVER_500);
          } else if (status >= 400 && status <= 500) {
            showError(ERROR_MESSAGES.SERVER_400);
          } else {
            showError(ERROR_MESSAGES.UPDATE_COMPANY_FAILED);
          }
        } else {
          showError(`${error.message || ERROR_MESSAGES.UNKNOWN}.`);
        }
      },
    });
  };

  if (isContactError) {
    return (
      <Box p={2} color="error.main">
        Failed to load contact information.
      </Box>
    );
  }

  return (
    <Formik
      initialValues={contactData || initialContactInfo}
      validationSchema={contactInfoFormValidations}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ isSubmitting, resetForm }) => (
        <Form autoComplete="off">
          <Wrapper>
            {/* Header */}
            <Box
              display="flex"
              alignItems="flex-start"
              justifyContent="space-between"
            >
              <Box sx={{ marginBottom: '20px' }}>
                <FormHeading
                  variant="h5"
                >
                  Contact information
                </FormHeading>
              </Box>
            </Box>
            {/* Form Fields */}
            <ContactFormFields />
            {/* Navigation Buttons */}
            <Box
              sx={{
                mt: 4,
                display: "flex",
                gap: 2,
                justifyContent: "flex-start",
              }}
            >
              <Button
                variant="outlined"
                onClick={() => {
                  dispatch(openInvoices())
                }}
                aria-label="Cancel"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                aria-label="Save and proceed"
                disabled={isSubmitting}
              >
                Save
              </Button>
            </Box>
          </Wrapper>
        </Form>
      )}
    </Formik>
  );
};

export default React.memo(ContactForm);
